import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import api from "./api/api";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

const ACCESS_TOKEN_KEY = "access";
const REFRESH_TOKEN_KEY = "refresh";
const USER_KEY = "user";

const fetchUserProfile = async (accessToken = null) => {
  const token =
    accessToken || localStorage.getItem(ACCESS_TOKEN_KEY);

  if (!token) {
    return null;
  }

  try {
    const response = await api.get("/api/user-profile/", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      "[AuthContext] Failed to fetch user profile:",
      error
    );

    return null;
  }
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (!refreshToken) {
    return null;
  }

  try {
    const response = await api.post(
      "/api/token/refresh/",
      {
        refresh: refreshToken,
      }
    );

    const newAccessToken = response.data?.access;

    if (!newAccessToken) {
      console.error(
        "[AuthContext] Refresh response did not contain an access token."
      );

      return null;
    }

    localStorage.setItem(
      ACCESS_TOKEN_KEY,
      newAccessToken
    );

    return newAccessToken;
  } catch (error) {
    console.error(
      "[AuthContext] Silent token refresh failed:",
      error
    );

    return null;
  }
};

const isTokenExpired = (token) => {
  if (!token) {
    return true;
  }

  try {
    const decoded = jwtDecode(token);

    if (!decoded?.exp) {
      return true;
    }

    return decoded.exp * 1000 <= Date.now();
  } catch (error) {
    console.error(
      "[AuthContext] Invalid JWT:",
      error
    );

    return true;
  }
};

const clearAuthentication = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const loadUser = useCallback(async () => {
    setInitializing(true);

    try {
      let accessToken =
        localStorage.getItem(ACCESS_TOKEN_KEY);

      const refreshToken =
        localStorage.getItem(REFRESH_TOKEN_KEY);

      /*
       * No access token and no refresh token means
       * the user is genuinely logged out.
       */
      if (!accessToken && !refreshToken) {
        setUser(null);
        return;
      }

      /*
       * If the access token is missing or expired,
       * attempt to obtain a new one using the refresh token.
       */
      if (
        !accessToken ||
        isTokenExpired(accessToken)
      ) {
        if (!refreshToken) {
          clearAuthentication();
          setUser(null);
          return;
        }

        accessToken = await refreshAccessToken();

        if (!accessToken) {
          clearAuthentication();
          setUser(null);
          return;
        }
      }

      /*
       * Access token is valid.
       * Now restore the user's profile.
       */
      const profile = await fetchUserProfile(
        accessToken
      );

      /*
       * If the profile request failed, try one more
       * token refresh. This handles cases where the
       * backend rejects an otherwise apparently valid token.
       */
      if (!profile) {
        const refreshedToken =
          await refreshAccessToken();

        if (refreshedToken) {
          const refreshedProfile =
            await fetchUserProfile(
              refreshedToken
            );

          if (refreshedProfile) {
            setUser(refreshedProfile);
            localStorage.setItem(
              USER_KEY,
              JSON.stringify(refreshedProfile)
            );
            return;
          }
        }

        clearAuthentication();
        setUser(null);
        return;
      }

      /*
       * Authentication successfully restored.
       */
      setUser(profile);

      localStorage.setItem(
        USER_KEY,
        JSON.stringify(profile)
      );
    } catch (error) {
      console.error(
        "[AuthContext] Authentication restoration error:",
        error
      );

      setUser(null);
    } finally {
      setInitializing(false);
    }
  }, []);

  /*
   * Restore authentication whenever the React
   * application starts/reloads.
   */
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /*
   * Silent access-token refresh.
   */
  useEffect(() => {
    const refreshIfNecessary = async () => {
      const accessToken =
        localStorage.getItem(ACCESS_TOKEN_KEY);

      const refreshToken =
        localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!accessToken || !refreshToken) {
        return;
      }

      try {
        const decoded = jwtDecode(accessToken);

        if (!decoded?.exp) {
          return;
        }

        const expirationTime =
          decoded.exp * 1000;

        const currentTime = Date.now();

        const timeRemaining =
          expirationTime - currentTime;

        /*
         * Refresh when the token has less than
         * 60 seconds remaining.
         */
        if (timeRemaining < 60 * 1000) {
          await refreshAccessToken();
        }
      } catch (error) {
        console.error(
          "[AuthContext] Token refresh check failed:",
          error
        );
      }
    };

    refreshIfNecessary();

    const interval = setInterval(
      refreshIfNecessary,
      4 * 60 * 1000
    );

    return () => {
      clearInterval(interval);
    };
  }, []);

  /*
   * Listen for login/authentication events.
   */
  useEffect(() => {
    const onAuthUpdated = () => {
      loadUser();
    };

    const onForceLogout = () => {
      clearAuthentication();
      setUser(null);

      /*
       * Only redirect when the application explicitly
       * requests a force logout.
       */
      if (window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    };

    window.addEventListener(
      "auth-updated",
      onAuthUpdated
    );

    window.addEventListener(
      "force-logout",
      onForceLogout
    );

    return () => {
      window.removeEventListener(
        "auth-updated",
        onAuthUpdated
      );

      window.removeEventListener(
        "force-logout",
        onForceLogout
      );
    };
  }, [loadUser]);

  const logout = useCallback(async () => {
    const refreshToken =
      localStorage.getItem(REFRESH_TOKEN_KEY);

    try {
      if (refreshToken) {
        await api.post("/api/logout/", {
          refresh_token: refreshToken,
        });
      }
    } catch (error) {
      console.error(
        "[AuthContext] Logout error:",
        error
      );
    } finally {
      clearAuthentication();
      setUser(null);

      window.location.replace("/login");
    }
  }, []);

  const value = {
    user,
    setUser,
    logout,
    initializing,
    refreshUser: loadUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };

